import React, { useState } from 'react'

import { Box } from '../box'
import { DateTimeInput } from '../date-time-input'
import { Popover, PopoverBackdrop, PopoverContent } from '../popover'
import { TimePicker } from '../time-picker'

export interface DurationPickerProps {
    /** Duration in minutes. */
    minutes?: number
    onChange: (minutes: number) => void
    label?: string
    required?: boolean
    placeholder?: string
    disabled?: boolean
    className?: string
    testID?: string
}

const pad2 = (value: number): string => value.toString().padStart(2, '0')
const formatDuration = (total: number): string =>
    `${pad2(Math.floor(total / 60))}:${pad2(total % 60)}`

const parseDuration = (text: string): number | null => {
    const match = /^(\d{1,2}):([0-5]?\d)$/.exec(text.trim())
    if (!match) {
        return null
    }
    return parseInt(match[1], 10) * 60 + parseInt(match[2], 10)
}

/**
 * HH:mm duration field with the dark {@link TimePicker} spinner in an anchored
 * popover. Controlled via `minutes`; free-typed text commits on blur or enter.
 */
export const DurationPicker: React.FC<DurationPickerProps> = ({
    minutes,
    onChange,
    label,
    required,
    placeholder,
    disabled = false,
    className,
    testID,
}) => {
    const current = minutes ?? null
    const [isOpen, setIsOpen] = useState(false)
    const [text, setText] = useState<string>(current !== null ? formatDuration(current) : '')

    const [prevMinutes, setPrevMinutes] = useState<number | null>(current)
    if (current !== prevMinutes) {
        setPrevMinutes(current)
        setText(current !== null ? formatDuration(current) : '')
    }

    const commitText = () => {
        const parsed = parseDuration(text)
        if (parsed !== null) {
            onChange(parsed)
        } else {
            setText(current !== null ? formatDuration(current) : '')
        }
    }

    const hours = Math.floor((current ?? 0) / 60)
    const mins = (current ?? 0) % 60

    return (
        <Popover
            isOpen={isOpen && !disabled}
            onClose={() => setIsOpen(false)}
            trapFocus={false}
            focusScope={false}
            placement='bottom left'
            trigger={triggerProps => (
                <Box {...triggerProps} className={className}>
                    <DateTimeInput
                        type='time'
                        label={label}
                        required={required}
                        editable={!disabled}
                        value={text}
                        placeholder={placeholder}
                        state={disabled ? 'disabled' : 'default'}
                        className='w-full'
                        testID={testID}
                        onChangeText={setText}
                        onSubmit={commitText}
                        onIconPress={() => setIsOpen(open => !open)}
                    />
                </Box>
            )}
        >
            <PopoverBackdrop />
            <PopoverContent className='rounded border border-solid border-border-subtle bg-background-elevation p-3'>
                <TimePicker
                    hours={hours}
                    minutes={mins}
                    showCalendarIcon={false}
                    onHoursChange={next => onChange(next * 60 + mins)}
                    onMinutesChange={next => onChange(hours * 60 + next)}
                />
            </PopoverContent>
        </Popover>
    )
}

DurationPicker.displayName = 'DurationPicker'
